<template>
    <div class="vue-need-bootstrap h100">
        <div class="d-flex justify-content-center w-100 h-100 overflow-auto">
            <div v-if="!loading" class="app-container d-flex flex-column h-100">
                <div class="text-center nav-selector">
                    <div class="link" :class="{ active: currentPage == 'account' }" @click="currentPage = 'account'">
                        Account
                    </div>
                    <span :class="{ 'd-none': !hasManageIntegrations }"
                        >|
                        <div
                            class="link"
                            :class="{ active: currentPage == 'integrations' }"
                            @click="currentPage = 'integrations'"
                        >
                            Integrations
                        </div>
                    </span>

                    <span :class="{ 'd-none': !displayBillingPanel }"
                        >|
                        <div
                            class="link"
                            :class="{ active: currentPage == 'billing' }"
                            @click="currentPage = 'billing'"
                        >
                            Billing
                        </div>
                    </span>

                    <span :class="{ 'd-none': !hasPODBilling }"
                        >|
                        <div
                            class="link"
                            :class="{ active: currentPage == 'podBilling' }"
                            @click="currentPage = 'podBilling'"
                        >
                            POD Billing
                        </div>
                    </span>

                    <span :class="{ 'd-none': !hasTeam }"
                        >|
                        <div class="link" :class="{ active: currentPage == 'team' }" @click="currentPage = 'team'">
                            Team
                        </div>
                    </span>
                </div>
                <div v-if="user" class="container d-flex flex-grow-1 h-100 w-100">
                    <transition name="fade" mode="out-in">
                        <!-- Account -->
                        <div v-if="currentPage == 'account'" key="account" class="w-100">
                            <account-component @user-updated="onUserUpdated" />
                        </div>

                        <!-- Integrations -->
                        <div v-if="currentPage == 'integrations'" key="integrations" class="w-100">
                            <integrations @user-updated="onUserUpdated" />
                        </div>

                        <!-- Billing -->
                        <div v-if="currentPage == 'billing'" key="billing" class="w-100">
                            <billing @user-updated="onUserUpdated" />
                        </div>

                        <!-- Billing -->
                        <div v-if="currentPage == 'podBilling'" key="podBilling" class="w-100">
                            <pod-billing @user-updated="onUserUpdated" />
                        </div>

                        <!-- Team -->
                        <div v-if="currentPage == 'team'" key="team" class="w-100">
                            <team @user-updated="onUserUpdated" />
                        </div>
                    </transition>
                </div>
            </div>
            <div v-if="loading" class="d-flex justify-content-center w-100 h-100 align-items-center">
                <b-spinner v-show="loading" variant="primary" class="mr-2" />
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { Ref, computed, onMounted, ref, watch } from 'vue';
import store from '@/store';
import Billing from '@/views/Account/Billing.vue';
import Integrations from '@/views/Account/Integrations.vue';
import AccountComponent from '@/views/Account/Settings.vue';
import PodBilling from '@/views/Account/PodBilling.vue';
import Team from '@/views/Account/Team.vue';
import { hasIntegrationAccess } from '@/api/user';
import { StoreType } from '@/typings/user';

const emit = defineEmits(['user-updated']);

const currentPage: Ref<'account' | 'billing' | 'integrations' | 'podBilling' | 'team'> = ref('account');
const loading = ref(true);

const user = computed(() => store.state.user);
const isImpersonate = computed(() => user.value?.ImpersonatedFromChildUser);
const isCollectiveRetailUser = computed(
    () => user.value?.AccessRoles?.Serialized?.split(',')?.includes('collective-retail') === true
);

const manageIntegrations = ref(true);

watch(currentPage, () => {
    if (!displayBillingPanel.value && currentPage.value === 'billing') {
        currentPage.value = 'account';
    }

    // If we don't have a customer, don't let access the Pod Billing section
    if (!hasPODBilling.value && currentPage.value === 'podBilling') {
        currentPage.value = 'account';
    }

    // If don't have access to integrations, don't let access integrations tab
    if (!hasManageIntegrations.value && currentPage.value === 'integrations') {
        currentPage.value = 'account';
    }

    // If don't have access to team, don't let access team tab
    if (!hasTeam.value && currentPage.value === 'team') {
        currentPage.value = 'account';
    }

    // Set the corresponding query parameter depending on which section we currently are
    const url = new URL(location.href);
    url.searchParams.set('section', currentPage.value);
    window.history.replaceState({}, document.title, `${url.pathname}?${url.searchParams.toString()}`);
});

const displayBillingPanel = computed(() => {
    if (isCollectiveRetailUser.value) return false;
    // We only show the billing panel if the store has a Stripe subscription or if it’s a Shopify store on the new plan.
    return hasBilling.value || hasShopifyBilling.value;
});

// Shopify stores using the new plan
const hasShopifyBilling = computed(() => {
    // Disable billing section for impersonated child users
    if (isImpersonate.value) {
        return false;
    }
    //sh new plan
    return user.value?.Stores.some((s) => s.Type === StoreType.Shopify && !s.HasBilling);
});

// Stores with stripe subscription (Etsy, Woo, or Shopify using the old plan, etc)
const hasBilling = computed(() => {
    // Disable billing section for impersonated child users
    if (isImpersonate.value) {
        return false;
    }

    return user.value?.Stores.some((s) => s.HasBilling);
});

const hasTeam = computed(() => !isCollectiveRetailUser.value);

const hasPODBilling = computed(() => {
    if (isCollectiveRetailUser.value) return false;
    // Disable billing section for impersonated child users
    if (isImpersonate.value) {
        return false;
    }

    return !hasBilling.value && user.value?.Stores.some((s) => s.HasPODBilling);
});

const hasAffiliate = computed(() => {
    if (user.value?.AccessRoles == null) return false;
    return user.value?.AccessRoles?.Serialized.includes('affiliate');
});

const hasManageIntegrations = computed(() => {
    if (isCollectiveRetailUser.value) return false;
    if (isImpersonate.value) {
        return manageIntegrations.value;
    }

    if (hasAffiliate.value) {
        return user.value?.Stores?.some((s) => !s.Disabled) ?? false;
    }

    return true;
});

onMounted(async () => {
    try {
        const url = new URL(location.href);
        const section = url.searchParams.get('section');
        if (section) {
            switch (section.toLowerCase()) {
                case 'billing':
                    currentPage.value = 'billing';
                    break;
                case 'integrations':
                    currentPage.value = 'integrations';
                    break;
                case 'podBilling':
                    currentPage.value = 'podBilling';
                    break;
                case 'team':
                    currentPage.value = 'team';
                    break;
                default:
                    currentPage.value = 'account';
            }
        }

        manageIntegrations.value = await hasIntegrationAccess();
    } finally {
        loading.value = false;
    }
});

const onUserUpdated = () => {
    emit('user-updated');
};
</script>
